import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Badge, Spinner, Alert, Form, Row, Col, Button, ButtonGroup, Table, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight, faSync, faFileExcel, faCubes, faCartPlus, faTriangleExclamation,
  faLock, faPen, faTrash, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getKits, getKit, getKitOrders, exportKits, removeKit } from '../services/api';

/*
 * Kits: the customer's kit recipes, how many of each current stock can build,
 * and the kit orders placed for them. A kit is defined by the warehouse; the
 * numbers here are worked out live from stock on every load, the same way the
 * warehouse's own kit screen works them out.
 */

// ── Shared table styling (as on Purchase Orders) ──────────────────────────────
const TH_STYLE = {
  background: '#212529', color: '#fff', fontWeight: 600, fontSize: '0.8rem',
  whiteSpace: 'nowrap', padding: '8px 10px', border: 'none', borderBottom: '2px solid #495057',
};
const TD_STYLE = { padding: '7px 10px', verticalAlign: 'middle', fontSize: '0.875rem', borderBottom: '1px solid #dee2e6' };
const TABLE_STYLE = { tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse', margin: 0 };
const SUB_TH = { background: '#e9ecef', padding: '5px 8px', whiteSpace: 'nowrap' };
const SUB_TD = { padding: '4px 8px' };

const KIT_COLS = [
  { key: 'expand', label: '', width: '3%', align: 'center' },
  { key: 'kit', label: 'Kit', width: '14%', align: 'left' },
  { key: 'desc', label: 'Description', width: '25%', align: 'left' },
  { key: 'components', label: 'Components', width: '9%', align: 'right' },
  { key: 'buildable', label: 'Can Build Now', width: '11%', align: 'right' },
  { key: 'limited', label: 'Limited By', width: '18%', align: 'left' },
  { key: 'single', label: 'As One Order', width: '11%', align: 'left' },
  { key: 'action', label: '', width: '9%', align: 'right' },
];

const ORDER_COLS = [
  { key: 'expand', label: '', width: '3%', align: 'center' },
  { key: 'date', label: 'Ordered', width: '15%', align: 'left' },
  { key: 'kit', label: 'Kit', width: '16%', align: 'left' },
  { key: 'qty', label: 'Kits', width: '8%', align: 'right' },
  { key: 'status', label: 'Status', width: '16%', align: 'left' },
  { key: 'ref', label: 'Customer Ref', width: '18%', align: 'left' },
  { key: 'orders', label: 'Pick Orders', width: '12%', align: 'right' },
  { key: 'source', label: 'Placed In', width: '12%', align: 'left' },
];

const STATUS_BG = {
  'Awaiting approval': 'warning',
  'Rejected': 'danger',
  'Shipped': 'success',
  'Partly shipped': 'info',
  'Being picked': 'primary',
  'Ordered': 'primary',
  'Cancelled': 'dark',
};

function StatusBadge({ status }) {
  return <Badge bg={STATUS_BG[status] || 'secondary'} text={status === 'Awaiting approval' ? 'dark' : undefined}>{status}</Badge>;
}

function ColGroup({ cols }) {
  return <colgroup>{cols.map(c => <col key={c.key} style={{ width: c.width }} />)}</colgroup>;
}

function HeaderTable({ cols }) {
  return (
    <div style={{ flexShrink: 0, overflowX: 'auto', background: '#212529' }}>
      <table style={TABLE_STYLE}>
        <ColGroup cols={cols} />
        <thead>
          <tr>{cols.map(c => <th key={c.key} style={{ ...TH_STYLE, textAlign: c.align }}>{c.label}</th>)}</tr>
        </thead>
      </table>
    </div>
  );
}

const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ── One kit, component by component ───────────────────────────────────────────
function KitDetail({ kit, canOrder, onOrder, canManage, onEdit, onRemove }) {
  const [planQty, setPlanQty] = useState(Math.max(kit.buildable, 1));
  const qty = Math.max(parseInt(planQty, 10) || 0, 0);
  const rows = kit.components.map(c => {
    const need = c.qtyPer * qty;
    return { ...c, need, short: Math.max(need - c.available, 0) };
  });
  const shortCount = rows.filter(r => r.short > 0).length;
  const locked = kit.openOrders > 0;

  return (
    <div style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '10px 16px' }}>
      {canManage && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem' }}>
          {locked
            ? <span style={{ color: '#92400e' }}>
                <FontAwesomeIcon icon={faLock} className="me-1" />
                {kit.openOrders} open order{kit.openOrders === 1 ? '' : 's'}: this kit can&apos;t be changed until
                {kit.openOrders === 1 ? ' it ships or is rejected.' : ' they ship or are rejected.'}
              </span>
            : <span className="text-muted">{kit.kitDesc || 'No description.'}</span>}
          <span className="d-flex gap-2 ms-auto">
            <Button size="sm" variant="outline-secondary" disabled={locked} onClick={() => onEdit(kit)}>
              <FontAwesomeIcon icon={faPen} className="me-1" />Edit Kit
            </Button>
            <Button size="sm" variant="outline-danger" disabled={locked} onClick={() => onRemove(kit)}>
              <FontAwesomeIcon icon={faTrash} className="me-1" />Remove
            </Button>
          </span>
        </div>
      )}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2" style={{ fontSize: '0.85rem' }}>
        <span>
          <strong>{fmtQty(kit.buildable)}</strong> can be built now
          {kit.bestSite
            ? <> · up to <strong>{fmtQty(kit.bestSite.buildable)}</strong> ship as one order from {kit.bestSite.warehouseName}</>
            : kit.buildable > 0 && <> · ships as separate orders from each warehouse</>}
        </span>
        <span className="d-flex align-items-center gap-2 ms-auto">
          <span className="text-muted">Plan for</span>
          <Form.Control
            size="sm" type="number" min={0} value={planQty}
            onChange={e => setPlanQty(e.target.value)}
            style={{ width: 90 }} aria-label="Kits to plan for"
          />
          <span className="text-muted">kits:</span>
          {shortCount === 0
            ? <Badge bg="success">All components in stock</Badge>
            : <Badge bg="danger"><FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />{shortCount} short</Badge>}
          {canOrder && (
            <Button size="sm" variant="primary" disabled={kit.buildable === 0} onClick={() => onOrder(kit, Math.min(qty || 1, kit.buildable))}>
              <FontAwesomeIcon icon={faCartPlus} className="me-1" />Order Kits
            </Button>
          )}
        </span>
      </div>

      <Table size="sm" bordered className="mb-0 bg-white" style={{ fontSize: '0.8rem' }}>
        <thead>
          <tr>
            {['SKU', 'Description', 'Unit', 'Per Kit', 'Available', 'Kits It Allows', `Need for ${fmtQty(qty)}`, 'Short', 'In Stock At', 'Supplier', 'Supplier PN']
              .map((h, i) => <th key={h} className={i >= 3 && i <= 7 ? 'text-end' : ''} style={SUB_TH}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.idCatalog} style={c.limiting ? { background: '#fff7e6' } : undefined}>
              <td style={SUB_TD}><code>{c.itemNumber}</code>{c.limiting && <Badge bg="warning" text="dark" className="ms-1">limits</Badge>}</td>
              <td style={SUB_TD}>{c.itemName || '—'}</td>
              <td style={SUB_TD}>{c.unit || '—'}</td>
              <td className="text-end" style={SUB_TD}>{fmtQty(c.qtyPer)}</td>
              <td className="text-end" style={SUB_TD}>{fmtQty(c.available)}</td>
              <td className="text-end" style={SUB_TD}>{fmtQty(c.kitsAllowed)}</td>
              <td className="text-end" style={SUB_TD}>{fmtQty(c.need)}</td>
              <td className="text-end" style={{ ...SUB_TD, color: c.short > 0 ? '#dc2626' : undefined, fontWeight: c.short > 0 ? 700 : undefined }}>
                {c.short > 0 ? fmtQty(c.short) : '—'}
              </td>
              <td style={SUB_TD}>
                {c.byWarehouse.length === 0
                  ? <span className="text-muted">None</span>
                  : c.byWarehouse.map(w => `${w.warehouseName} ${fmtQty(w.available)}`).join(', ')}
              </td>
              <td style={SUB_TD}>{c.supplier || '—'}</td>
              <td style={SUB_TD}>{c.supplierPN || '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ── Kits tab ───────────────────────────────────────────────────────────────────
function KitsTab({ canOrder, onOrder, canManage, onEdit, reloadKey }) {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const detailCache = useRef({});
  const [, forceUpdate] = useState(0);
  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    detailCache.current = {};
    try {
      setKits(await getKits());
    } catch {
      setError('Failed to load kits. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const toggleExpand = useCallback((id) => {
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next !== null && !detailCache.current[next]) {
        detailCache.current[next] = { loading: true };
        getKit(next)
          .then(kit => { detailCache.current[next] = { kit }; forceUpdate(n => n + 1); })
          .catch(() => { detailCache.current[next] = { error: 'Failed to load this kit.' }; forceUpdate(n => n + 1); });
      }
      return next;
    });
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return kits;
    return kits.filter(k => k.kitName.toLowerCase().includes(q) || (k.kitDesc || '').toLowerCase().includes(q));
  }, [kits, search]);

  const confirmRemove = async () => {
    setRemoveBusy(true);
    try {
      const result = await removeKit(removing.idKit);
      toast.success(result.outcome === 'retired'
        ? `Kit ${removing.kitName} removed. Its past orders stay in Kit Orders.`
        : `Kit ${removing.kitName} removed`);
      setRemoving(null);
      setExpandedId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove the kit', { duration: 8000 });
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <>
      <div style={{ flexShrink: 0, padding: '0 0 8px' }}>
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={5} md={4}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <Form.Control type="text" size="sm" placeholder="Kit name or description…" value={search} onChange={e => setSearch(e.target.value)} />
          </Col>
          <Col xs="auto" className="ms-auto">
            <span className="text-muted small">{shown.length} kit{shown.length !== 1 ? 's' : ''}</span>
          </Col>
        </Row>
        {error && <Alert variant="danger" className="mt-2 mb-0 py-2">{error}</Alert>}
      </div>

      {!loading && shown.length > 0 && <HeaderTable cols={KIT_COLS} />}

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Working out what can be built…</p>
          </div>
        ) : shown.length === 0 ? (
          <Alert variant="info" className="mt-3">
            {kits.length > 0 ? 'No kits match your search.'
              : canManage ? 'No kits yet. Use New Kit to build one from items in your catalog.'
                : 'No kits are set up for your account.'}
          </Alert>
        ) : (
          <table style={{ ...TABLE_STYLE, background: '#fff' }}>
            <ColGroup cols={KIT_COLS} />
            <tbody>
              {shown.map(kit => {
                const isOpen = expandedId === kit.idKit;
                const cached = detailCache.current[kit.idKit];
                const rowBg = isOpen ? '#e8f0fe' : undefined;
                return (
                  <React.Fragment key={kit.idKit}>
                    <tr
                      onClick={() => toggleExpand(kit.idKit)}
                      style={{ cursor: 'pointer', background: rowBg }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg || ''; }}
                    >
                      <td style={{ ...TD_STYLE, textAlign: 'center', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" />
                      </td>
                      <td style={{ ...TD_STYLE, fontWeight: 600 }}>
                        {kit.kitName}
                        {kit.openOrders > 0 && (
                          <FontAwesomeIcon icon={faLock} className="ms-2" style={{ color: '#b45309' }}
                            title={`${kit.openOrders} open order${kit.openOrders === 1 ? '' : 's'}: can't be changed until shipped or rejected`} />
                        )}
                      </td>
                      <td style={{ ...TD_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={kit.kitDesc || ''}>{kit.kitDesc || '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(kit.componentCount)}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                        <Badge bg={kit.buildable > 0 ? 'success' : 'secondary'} style={{ fontSize: '0.8rem' }}>{fmtQty(kit.buildable)}</Badge>
                      </td>
                      <td style={{ ...TD_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={kit.limitedBy.map(c => c.itemNumber).join(', ')}>
                        {kit.limitedBy.length === 0 ? '—'
                          : kit.limitedBy.length <= 2 ? kit.limitedBy.map(c => c.itemNumber).join(', ')
                            : `${kit.limitedBy.slice(0, 2).map(c => c.itemNumber).join(', ')} +${kit.limitedBy.length - 2}`}
                      </td>
                      <td style={TD_STYLE}>{kit.bestSite ? `${fmtQty(kit.bestSite.buildable)} · ${kit.bestSite.warehouseName}` : '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        {canOrder && (
                          <Button size="sm" variant="outline-primary" disabled={kit.buildable === 0} onClick={() => onOrder(kit, 1)} title="Order kits">
                            <FontAwesomeIcon icon={faCartPlus} />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={KIT_COLS.length} style={{ padding: 0, borderBottom: '1px solid #dee2e6' }}>
                          {!cached || cached.loading ? (
                            <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                          ) : cached.error ? (
                            <Alert variant="danger" className="m-2 py-1 mb-1">{cached.error}</Alert>
                          ) : (
                            <KitDetail kit={cached.kit} canOrder={canOrder} onOrder={onOrder}
                              canManage={canManage} onEdit={onEdit} onRemove={setRemoving} />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal show={!!removing} onHide={() => !removeBusy && setRemoving(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Kit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Remove kit <strong>{removing?.kitName}</strong>?</p>
          <p className="text-muted small mb-0">
            It will no longer be listed or available to order. Your catalog items and stock are not affected,
            and any past orders of this kit stay in Kit Orders.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemoving(null)} disabled={removeBusy}>Cancel</Button>
          <Button variant="danger" onClick={confirmRemove} disabled={removeBusy}>
            {removeBusy ? <><Spinner animation="border" size="sm" className="me-1" />Removing…</> : <><FontAwesomeIcon icon={faTrash} className="me-1" />Remove Kit</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ── Kit orders tab ─────────────────────────────────────────────────────────────
function KitOrdersTab({ since, reloadKey, link }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getKitOrders(since)
      .then(list => { if (!cancelled) setOrders(list); })
      .catch(() => { if (!cancelled) setError('Failed to load kit orders. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [since, reloadKey]);

  return (
    <>
      {error && <Alert variant="danger" className="mb-2 py-2">{error}</Alert>}
      {!loading && orders.length > 0 && <HeaderTable cols={ORDER_COLS} />}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading kit orders…</p>
          </div>
        ) : orders.length === 0 ? (
          <Alert variant="info" className="mt-3">No kit orders since {new Date(`${since}T12:00:00`).toLocaleDateString()}.</Alert>
        ) : (
          <table style={{ ...TABLE_STYLE, background: '#fff' }}>
            <ColGroup cols={ORDER_COLS} />
            <tbody>
              {orders.map(g => {
                const isOpen = expanded === g.orderGroup;
                const rowBg = isOpen ? '#e8f0fe' : undefined;
                return (
                  <React.Fragment key={g.orderGroup}>
                    <tr
                      onClick={() => setExpanded(isOpen ? null : g.orderGroup)}
                      style={{ cursor: 'pointer', background: rowBg }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg || ''; }}
                    >
                      <td style={{ ...TD_STYLE, textAlign: 'center', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" />
                      </td>
                      <td style={TD_STYLE}>{fmtDateTime(g.createdAt)}</td>
                      <td style={{ ...TD_STYLE, fontWeight: 600 }}>{g.kitName}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(g.kitQty)}</td>
                      <td style={TD_STYLE}><StatusBadge status={g.status} /></td>
                      <td style={{ ...TD_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.customerRef || '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{g.orders.length}</td>
                      <td style={TD_STYLE}>{g.source === 'portal' ? 'Portal' : 'Warehouse'}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={ORDER_COLS.length} style={{ padding: 0, borderBottom: '1px solid #dee2e6' }}>
                          <div style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '10px 16px' }}>
                            <Table size="sm" bordered className="mb-0 bg-white" style={{ fontSize: '0.8rem' }}>
                              <thead>
                                <tr>{['Order #', 'Part', 'Ships From', 'Lines', 'Status'].map(h => <th key={h} style={SUB_TH}>{h}</th>)}</tr>
                              </thead>
                              <tbody>
                                {g.orders.map(o => (
                                  <tr key={o.idTrans}>
                                    <td style={SUB_TD}><Link to={link(`/orders/${o.idTrans}`)}>{o.idTrans}</Link></td>
                                    <td style={SUB_TD}>{o.partCount > 1 ? `${o.part} of ${o.partCount}` : '—'}</td>
                                    <td style={SUB_TD}>{o.warehouseName || '—'}</td>
                                    <td style={SUB_TD}>{fmtQty(o.lineCount)}</td>
                                    <td style={SUB_TD}><StatusBadge status={o.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function Kits() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { tenantPrefix } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'orders' ? 'orders' : 'kits';
  const [since, setSince] = useState(daysAgo(180));
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const canOrder = hasPermission('inventory', 'submit');
  // Changing what a kit is made of is for inventory approvers, as on the server.
  const canManage = hasPermission('inventory', 'approve');
  const link = useCallback((path) => (tenantPrefix ? `/${tenantPrefix}${path}` : path), [tenantPrefix]);
  const onOrder = useCallback((kit, qty) => navigate(link(`/kits/${kit.idKit}/order?qty=${qty}`)), [navigate, link]);
  const onEdit = useCallback((kit) => navigate(link(`/kits/${kit.idKit}/edit`)), [navigate, link]);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await exportKits(since);
      downloadBlob(response.data, 'kits_export.xlsx');
    } catch {
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flexShrink: 0, padding: '12px 0 8px', borderBottom: '1px solid #dee2e6', marginBottom: 8 }}>
        <Row className="g-2 align-items-end">
          <Col xs="auto">
            {/* Buttons rather than Nav pills: the portal's global nav styles are for the sidebar. */}
            <ButtonGroup size="sm" aria-label="Kits view">
              <Button variant={tab === 'kits' ? 'primary' : 'outline-primary'} onClick={() => setSearchParams({})}>
                <FontAwesomeIcon icon={faCubes} className="me-1" />Kits
              </Button>
              <Button variant={tab === 'orders' ? 'primary' : 'outline-primary'} onClick={() => setSearchParams({ tab: 'orders' })}>
                Kit Orders
              </Button>
            </ButtonGroup>
          </Col>
          {tab === 'orders' && (
            <Col xs={6} sm={3} md={2}>
              <Form.Label className="small fw-semibold mb-1">Since</Form.Label>
              <Form.Control type="date" size="sm" value={since} max={daysAgo(0)} onChange={e => e.target.value && setSince(e.target.value)} />
            </Col>
          )}
          {canManage && tab === 'kits' && (
            <Col xs="auto">
              <Button size="sm" variant="primary" onClick={() => navigate(link('/kits/new'))}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />New Kit
              </Button>
            </Col>
          )}
          <Col xs="auto" className="ms-auto">
            <Button size="sm" variant="outline-secondary" onClick={() => setReloadKey(k => k + 1)} title="Refresh">
              <FontAwesomeIcon icon={faSync} />
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-success" onClick={handleExport} disabled={exporting} title="Export kits, components and kit orders to Excel">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </Col>
        </Row>
        {exportError && <Alert variant="danger" className="mt-2 mb-0 py-2">{exportError}</Alert>}
      </div>

      {tab === 'kits'
        ? <KitsTab canOrder={canOrder} onOrder={onOrder} canManage={canManage} onEdit={onEdit} reloadKey={reloadKey} />
        : <KitOrdersTab since={since} reloadKey={reloadKey} link={link} />}
    </div>
  );
}
